import { Howl, Howler } from 'howler'

// =====================================================================
// AudioSystem — sistema de áudio do Capoeira Soul (web/WebView).
//
// Princípios:
// 1. Categorias separadas: music | sfx | ui, cada uma com volume próprio,
//    multiplicado pelo master. Tela de opções futura só precisa chamar
//    setVolume(cat, value) — toda a propagação acontece aqui.
// 2. Fades suaves em playMusic (cross-fade ~600ms) para transições entre
//    menu, fase e resultado. Opt-out via { fadeMs: 0 }.
// 3. Cooldown opcional por chave de SFX, registrado no loadSFX. Evita
//    crackle quando hits empilham (combos, multi-impactos).
// 4. Persistência de volumes em localStorage (chave cs_audio_vol).
// 5. Unlock por gesto + mute em visibilitychange via installAudioLifecycle.
//
// Decisão html5:true vs Web Audio:
// - Music sempre com html5:true (streaming). Justificativa:
//     * arquivos longos em loop (até 4.4 MB), decodificar inteiro em
//       memória custa RAM em mobile (Web Audio decodifica para PCM 32-bit);
//     * não precisamos de polifonia para música — tocamos uma só por vez;
//     * Howl.fade() funciona em ambos os modos, então fade-out/in não regride.
// - SFX e UI sempre com html5:false (Web Audio). Justificativa:
//     * latência muito menor (<10ms vs 80-150ms do <audio>);
//     * polifonia real (vários hits simultâneos no combate);
//     * vai pelo AudioContext compartilhado, então Howler.mute() funciona.
// =====================================================================

export type AudioCategory = 'music' | 'sfx' | 'ui'

export interface AudioVolumes {
  master: number
  music: number
  sfx: number
  ui: number
}

const STORAGE_KEY = 'cs_audio_vol'
const DEFAULT_VOLUMES: AudioVolumes = { master: 1, music: 0.55, sfx: 0.85, ui: 0.7 }
const FADE_DEFAULT_MS = 600

interface SFXEntry {
  howl: Howl
  cooldownMs: number
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0
  return Math.max(0, Math.min(1, v))
}

export class AudioSystem {
  private music: Map<string, Howl> = new Map()
  private sfx: Map<string, SFXEntry> = new Map()
  private ui: Map<string, SFXEntry> = new Map()

  private currentMusic: Howl | null = null
  private currentMusicKey: string | null = null
  private fadeOutTimers: Map<Howl, number> = new Map()

  private lastPlayedAt: Map<string, number> = new Map()
  private vol: AudioVolumes = { ...DEFAULT_VOLUMES }
  private mutedByVisibility = false
  private haptics = true
  // Reprodução de vídeos (intros de fase, cutscenes de herói/epílogo).
  // Persistido junto do payload de volumes. Quando false, as cenas pulam
  // o load.video e caem no fallback de imagem/texto.
  private videosEnabled = true

  // listeners para futura tela de opções (useSyncExternalStore-friendly).
  private volumeListeners: Set<() => void> = new Set()

  constructor() {
    this.loadVolumesFromStorage()
  }

  // ===== Loading =====

  loadMusic(key: string, url: string, opts?: { html5?: boolean; preload?: boolean; loop?: boolean }) {
    const html5 = opts?.html5 ?? true
    const sound = new Howl({
      src: [url],
      loop: opts?.loop ?? true,
      volume: 0,
      html5,
      preload: opts?.preload ?? true,
    })
    this.music.set(key, sound)
  }

  // Duração da música em ms; retorna null se a faixa ainda não foi
  // baixada (Howler reporta 0 antes do load). Usado pela IntroScene
  // para sincronizar o typewriter com a narração.
  getMusicDurationMs(key: string): number | null {
    const m = this.music.get(key)
    if (!m) return null
    const d = m.duration()
    return d > 0 ? d * 1000 : null
  }

  // Registra callback de fim de faixa (one-shot). Retorna unsubscribe.
  onMusicEnd(key: string, cb: () => void): () => void {
    const m = this.music.get(key)
    if (!m) return () => {}
    m.once('end', cb)
    return () => m.off('end', cb)
  }

  // Registra callback de carregamento (dispara imediatamente se já
  // carregada). Retorna unsubscribe.
  onMusicLoad(key: string, cb: () => void): () => void {
    const m = this.music.get(key)
    if (!m) return () => {}
    if (m.state() === 'loaded') {
      cb()
      return () => {}
    }
    m.once('load', cb)
    return () => m.off('load', cb)
  }

  loadSFX(
    key: string,
    url: string,
    opts?: { cooldownMs?: number; volume?: number; preload?: boolean; html5?: boolean; loop?: boolean },
  ) {
    const sound = new Howl({
      src: [url],
      volume: opts?.volume ?? 1,
      preload: opts?.preload ?? true,
      // html5:true para narrações longas (lore) — streaming, mais confiável
      // em WebView Android. SFX curtos mantêm Web Audio (html5:false) para
      // latência mínima.
      html5: opts?.html5 ?? false,
      loop: opts?.loop ?? false,
    })
    this.sfx.set(key, { howl: sound, cooldownMs: opts?.cooldownMs ?? 0 })
  }

  unloadSFX(key: string) {
    const entry = this.sfx.get(key)
    if (entry) {
      entry.howl.unload()
      this.sfx.delete(key)
    }
  }

  unloadMusic(key: string) {
    const sound = this.music.get(key)
    if (!sound) return

    if (this.currentMusicKey === key) {
      this.currentMusic = null
      this.currentMusicKey = null
    }
    const timer = this.fadeOutTimers.get(sound)
    if (timer != null) {
      window.clearTimeout(timer)
      this.fadeOutTimers.delete(sound)
    }
    sound.unload()
    this.music.delete(key)
  }

  unloadUnusedMenuMusic(activeLocale: string) {
    const allMenuMusic = ['berimbau', 'capoeira_soul_en', 'alma_de_capoeira_es']
    const activeMusic = activeLocale === 'pt' ? 'berimbau' 
                      : activeLocale === 'en' ? 'capoeira_soul_en' 
                      : 'alma_de_capoeira_es'
                      
    allMenuMusic.forEach(key => {
      if (key !== activeMusic) {
        this.unloadMusic(key)
      }
    })
  }

  loadUI(key: string, url: string, opts?: { cooldownMs?: number; volume?: number; preload?: boolean }) {
    const sound = new Howl({
      src: [url],
      volume: opts?.volume ?? 1,
      preload: opts?.preload ?? true,
      html5: false,
    })
    this.ui.set(key, { howl: sound, cooldownMs: opts?.cooldownMs ?? 0 })
  }

  // ===== Playback =====

  playMusic(key: string, opts?: { fadeMs?: number; force?: boolean }) {
    const fadeMs = opts?.fadeMs ?? FADE_DEFAULT_MS
    const next = this.music.get(key)
    if (!next) return

    // Mesma música já tocando — comportamento default: não reinicia, só
    // garante volume correto (evita "skip" perceptível quando o menu e a
    // fase compartilham trilha). Com `force:true`, força stop + restart
    // pra marcar transição entre cenários que casualmente compartilham
    // a mesma faixa (ex: 3 fases seguidas usando "swing").
    if (this.currentMusicKey === key && next.playing()) {
      if (!opts?.force) {
        next.volume(this.effective('music'))
        return
      }
      this.fadeOutAndStop(next, fadeMs)
      this.currentMusic = null
      this.currentMusicKey = null
    }

    // Fade-out da anterior (se houver e for diferente).
    if (this.currentMusic && this.currentMusic !== next) {
      this.fadeOutAndStop(this.currentMusic, fadeMs)
    }

    const targetVol = this.effective('music')
    if (!next.playing()) {
      next.volume(0)
      next.play()
    }
    if (fadeMs > 0) {
      next.fade(next.volume(), targetVol, fadeMs)
    } else {
      next.volume(targetVol)
    }

    this.currentMusic = next
    this.currentMusicKey = key
  }

  stopSFX(key: string) {
    const entry = this.sfx.get(key)
    if (entry && entry.howl.playing()) {
      entry.howl.stop()
    }
  }

  preloadSFX(key: string) {
    const entry = this.sfx.get(key)
    if (entry && entry.howl.state() === 'unloaded') {
      entry.howl.load()
    }
  }

  playSFX(
    key: string,
    opts?: { volume?: number; rate?: number; randomize?: boolean; vibrate?: number | number[]; duckTo?: number },
  ) {
    const entry = this.sfx.get(key)
    if (!entry) return
    if (this.shouldThrottle(`sfx:${key}`, entry.cooldownMs)) return

    const baseVol = this.effective('sfx')
    const finalVol = opts?.volume != null ? baseVol * opts.volume : baseVol

    entry.howl.volume(finalVol)

    // Pitch aleatório para evitar fadiga auditiva em sons repetitivos (ex: socos)
    const finalRate = opts?.randomize
      ? (opts.rate || 1) * (0.9 + Math.random() * 0.2)
      : (opts?.rate || 1)

    entry.howl.rate(finalRate)
    entry.howl.play()

    // Vibracao haptica: parametro explicito vence; fallback por nome
    // mantido para retrocompat (legacy callers que dependiam do "hit").
    if (opts?.vibrate !== undefined) {
      if (this.haptics && 'vibrate' in navigator) {
        navigator.vibrate(opts.vibrate)
      }
    } else if (key === 'hit_receive' && this.haptics && 'vibrate' in navigator) {
      navigator.vibrate(200) // Fallback hardcoded para dano
    }

    if (opts?.duckTo != null && this.currentMusic) {
      const targetMusic = this.currentMusic;
      const targetVol = this.effective('music') * opts.duckTo;
      targetMusic.fade(targetMusic.volume(), targetVol, 200);
      
      entry.howl.once('end', () => {
        if (this.currentMusic === targetMusic) {
          targetMusic.fade(targetMusic.volume(), this.effective('music'), 300);
        }
      });
    }
  }

  playUI(key: string, opts?: { volume?: number; rate?: number }) {
    const entry = this.ui.get(key)
    if (!entry) return
    if (this.shouldThrottle(`ui:${key}`, entry.cooldownMs)) return
    
    const baseVol = this.effective('ui')
    const finalVol = opts?.volume != null ? baseVol * opts.volume : baseVol

    entry.howl.volume(finalVol)
    if (opts?.rate != null) entry.howl.rate(opts.rate)
    entry.howl.play()
  }

  vibrate(pattern: number | number[]): void {
    if (!this.haptics) return
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      try {
        navigator.vibrate(pattern)
      } catch {
        /* Silencioso se o browser bloquear */
      }
    }
  }

  stopAll(opts?: { fadeMs?: number }) {
    const fadeMs = opts?.fadeMs ?? 250
    if (this.currentMusic) {
      this.fadeOutAndStop(this.currentMusic, fadeMs)
      this.currentMusic = null
      this.currentMusicKey = null
    }
    this.music.forEach((m) => {
      if (m.playing()) m.stop()
    })
    this.sfx.forEach((e) => e.howl.stop())
    this.ui.forEach((e) => e.howl.stop())
  }

  pauseMusic(): void {
    if (this.currentMusic && this.currentMusic.playing()) {
      this.currentMusic.pause()
    }
  }

  resumeMusic(): void {
    if (this.currentMusic) {
      this.currentMusic.play()
    }
  }

  // Tempo atual da música corrente em ms (para sincronia rítmica do RhythmScene).
  currentMusicMs(): number {
    if (!this.currentMusic || !this.currentMusic.playing()) return 0
    const pos = this.currentMusic.seek()
    return typeof pos === 'number' ? pos * 1000 : 0
  }

  isHapticsEnabled(): boolean {
    return this.haptics
  }

  setHapticsEnabled(enabled: boolean): void {
    if (this.haptics === enabled) return
    this.haptics = enabled
    this.persistVolumes()
    this.notifyVolumeChange()
  }

  isVideosEnabled(): boolean {
    return this.videosEnabled
  }

  setVideosEnabled(enabled: boolean): void {
    if (this.videosEnabled === enabled) return
    this.videosEnabled = enabled
    this.persistVolumes()
    this.notifyVolumeChange()
  }

  // ===== Volume =====

  setVolume(cat: keyof AudioVolumes, value: number) {
    const next = clamp01(value)
    if (this.vol[cat] === next) return
    // Imutável: nova referência só quando muda — necessário para
    // useSyncExternalStore não cair em loop infinito.
    this.vol = { ...this.vol, [cat]: next }
    this.applyMusicVolume()
    this.persistVolumes()
    this.notifyVolumeChange()
  }

  resetVolumes() {
    this.vol = { ...DEFAULT_VOLUMES }
    this.applyMusicVolume()
    this.persistVolumes()
    this.notifyVolumeChange()
  }

  // Retorna a referência estável do snapshot atual. Não copiar aqui:
  // useSyncExternalStore usa Object.is e copiar quebraria a estabilidade.
  getVolumes(): AudioVolumes {
    return this.vol
  }

  subscribeVolume(listener: () => void): () => void {
    this.volumeListeners.add(listener)
    return () => this.volumeListeners.delete(listener)
  }

  // ===== Mobile / WebView lifecycle =====

  resume(): void {
    const ctx = Howler.ctx
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {
        /* WebView pode rejeitar até o gesto chegar — ok ignorar */
      })
    }
  }

  setMutedByVisibility(muted: boolean): void {
    if (this.mutedByVisibility === muted) return
    this.mutedByVisibility = muted
    Howler.mute(muted)
  }

  // ===== Internals =====

  private effective(cat: AudioCategory): number {
    return clamp01(this.vol.master * this.vol[cat])
  }

  private applyMusicVolume(): void {
    if (!this.currentMusic) return
    this.currentMusic.volume(this.effective('music'))
  }

  private shouldThrottle(throttleKey: string, cooldownMs: number): boolean {
    if (cooldownMs <= 0) return false
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now())
    const last = this.lastPlayedAt.get(throttleKey) ?? -Infinity
    if (now - last < cooldownMs) return true
    this.lastPlayedAt.set(throttleKey, now)
    return false
  }

  private fadeOutAndStop(sound: Howl, fadeMs: number): void {
    const prevTimer = this.fadeOutTimers.get(sound)
    if (prevTimer != null) window.clearTimeout(prevTimer)
    if (fadeMs <= 0) {
      sound.stop()
      return
    }
    sound.fade(sound.volume(), 0, fadeMs)
    const t = window.setTimeout(() => {
      sound.stop()
      this.fadeOutTimers.delete(sound)
    }, fadeMs + 30)
    this.fadeOutTimers.set(sound, t)
  }

  private loadVolumesFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw) as Partial<AudioVolumes> & { haptics?: boolean; videosEnabled?: boolean }
      this.vol = {
        master: clamp01(parsed.master ?? DEFAULT_VOLUMES.master),
        music: clamp01(parsed.music ?? DEFAULT_VOLUMES.music),
        sfx: clamp01(parsed.sfx ?? DEFAULT_VOLUMES.sfx),
        ui: clamp01(parsed.ui ?? DEFAULT_VOLUMES.ui),
      }
      this.haptics = parsed.haptics ?? true
      this.videosEnabled = parsed.videosEnabled ?? true
    } catch {
      /* localStorage indisponível ou JSON inválido — usa defaults */
    }
  }

  private persistVolumes(): void {
    try {
      const payload = { ...this.vol, haptics: this.haptics, videosEnabled: this.videosEnabled }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    } catch {
      /* ignorar */
    }
  }

  private notifyVolumeChange(): void {
    this.volumeListeners.forEach((l) => {
      try { l() } catch { /* listener defensivo */ }
    })
  }
}

export const GlobalAudio = new AudioSystem()

// Liga o unlock e o lifecycle UMA vez por sessão. Chamado pelo main.tsx.
let installed = false
export function installAudioLifecycle(): void {
  if (installed) return
  installed = true

  // Limita o pool de <audio> HTML5 que o Howler aloca por Howl com html5:true.
  // WebView Android costuma travar quando passa de ~15 elementos simultaneos
  // (narracao do bestiario, ambient SFX do bioma, musica de menu, etc.).
  // Pinar em 10 da folga sem permitir explosao.
  Howler.html5PoolSize = 10

  const tryUnlock = () => GlobalAudio.resume()
  const events: Array<keyof DocumentEventMap> = ['pointerdown', 'touchstart', 'keydown']
  events.forEach((evt) => document.addEventListener(evt, tryUnlock, { passive: true }))

  document.addEventListener('visibilitychange', () => {
    GlobalAudio.setMutedByVisibility(document.hidden)
  })

  // iOS Safari emite pagehide sem visibilitychange ao trocar de aba;
  // Chrome WebView emite freeze ao congelar background tabs. Ambos
  // exigem mute explicito pra evitar audio continuar tocando surdo
  // (consumindo CPU e bateria) ate o SO matar a aba.
  window.addEventListener('pagehide', () => {
    GlobalAudio.setMutedByVisibility(true)
  })
  document.addEventListener('freeze', () => {
    GlobalAudio.setMutedByVisibility(true)
  })
  document.addEventListener('resume', () => {
    GlobalAudio.setMutedByVisibility(false)
  })
}
